import { message } from "antd";
import timeUtils from "../../../../utils/timeUtils";
import { formatFlightTime, isInCurfewTime, formatTimeWithZone } from "../../../../utils/timezoneUtils";
import { TIMEZONE_OPTIONS, getTimezoneByMode } from "../../../../contexts/TimeZoneContext";
import dayjs from "dayjs";
import { formatTimeToHHMM } from "../../../../utils/dateUtils";
import { masterDataService } from "../../../../api/enhancedApi";
import { getAirportConstraint, normalizeAirportCode } from "../../../../utils/airportCodeUtils";
import { getAirportMinGroundTime } from "../../../../utils/minGroundTimeUtils";
import {
    buildAircraftAvailabilityQuery,
    evaluateAircraftAvailability,
    getAircraftAvailabilityWindows,
    normalizeAircraftRegistration,
} from "../../shared/aircraftAvailabilityConstraints";

const AIRCRAFT_AVAILABILITY_TYPE_LABELS = {
    SCHEDULED_MAINTENANCE: "计划维修",
    UNSCHEDULED_MAINTENANCE: "非计划维修",
    MODIFICATION: "改装",
    LEASE_RETURN: "租约归还",
    RESERVATION: "预留",
    OTHER: "其他不可用",
};

function getAvailabilityWindowLabel(window = {}) {
    const type = String(window.window_type || "OTHER").trim().toUpperCase();
    const typeLabel = AIRCRAFT_AVAILABILITY_TYPE_LABELS[type] || AIRCRAFT_AVAILABILITY_TYPE_LABELS.OTHER;
    const title = String(window.title || "").trim();
    const airport = String(window.airport_code || "").trim().toUpperCase();
    return `${typeLabel}${title ? `（${title}${airport ? ` @ ${airport}` : ""}）` : airport ? `（@ ${airport}）` : ""}`;
}

/**
 * 约束服务 - 处理约束数据的加载和校验
 */
class ConstraintsService {
    /**
     * 转换宵禁规则字段名称
     * 将 start/end 字段转换为 start_time/end_time
     * @param {Array} rules - 宵禁规则数组
     * @returns {Array} 转换后的宵禁规则数组
     */
    static convertCurfewRuleFields(rules) {
        if (!rules || !Array.isArray(rules)) return [];

        return rules.map((rule) => {
            // 如果已经有 start_time/end_time 字段，直接返回
            if (rule.start_time && rule.end_time) {
                return rule;
            }

            // 如果有 start/end 字段但没有 start_time/end_time 字段，进行转换
            if (rule.start && rule.end) {
                return {
                    ...rule,
                    start_time: rule.start,
                    end_time: rule.end,
                };
            }

            // 返回原始规则
            return rule;
        });
    }

    /**
     * 加载机场约束信息
     * @param {Array<string>} airportCodes - 机场三字码数组
     * @param {Function} setConstraintsLoading - 设置加载状态的函数
     * @param {Function} setAirportConstraints - 设置机场约束的函数
     * @param {Function} setConstraintsError - 设置错误信息的函数
     */
    static async loadAirportConstraints(airportCodes, { setConstraintsLoading, setAirportConstraints, setConstraintsError }) {
        if (!airportCodes || airportCodes.length === 0) {
            return;
        }

        setConstraintsLoading(true);

        try {
            // 调用API获取数据
            const response = await masterDataService.airport.getAirportConstraints(); // 使用 masterDataService（方法挂在 airport 子对象下）

            // 确保响应数据是数组
            let allConstraints = [];
            if (response && Array.isArray(response.data)) {
                allConstraints = response.data;
            } else {
                console.warn("获取机场约束 API 未返回有效的数组数据:", response);
            }

            // 转换为以机场代码为键的对象格式
            const airportConstraintsMap = {};
            let foundCount = 0;

            // 只保留需要的机场约束
            airportCodes.forEach((rawCode) => {
                const code = normalizeAirportCode(rawCode);
                if (!code) {
                    console.warn("跳过空的机场代码");
                    return;
                }

                const constraint = allConstraints.find((item) => normalizeAirportCode(item.iata_code) === code);
                if (constraint) {
                    foundCount++;
                    airportConstraintsMap[code] = {
                        iata_code: code,
                        airport_name: constraint.airport_name || code,
                        timezone: constraint.timezone || "UTC",
                        dep_curfew_rules: this.convertCurfewRuleFields(constraint.dep_curfew_rules || []),
                        arr_curfew_rules: this.convertCurfewRuleFields(constraint.arr_curfew_rules || []),
                        turnaround_time: constraint.turnaround_time ?? null,
                        is_maintenance_base: constraint.is_maintenance_base || false,
                    };
                } else {
                    // 主数据里没有这个机场：只保留空宵禁占位，最小过站保持为空（不是 60），
                    // 由取值方按“缺配置”拦住，不得造一条“过站 1 小时”的假约束
                    airportConstraintsMap[code] = {
                        iata_code: code,
                        airport_name: code,
                        timezone: "UTC",
                        dep_curfew_rules: [],
                        arr_curfew_rules: [],
                        turnaround_time: null,
                        is_maintenance_base: false,
                    };
                    console.warn(`未找到机场 ${code} 的约束数据，最小过站保持为空`);
                }
            });

            // 检查是否为空对象
            if (Object.keys(airportConstraintsMap).length === 0) {
                console.warn("没有找到任何所需的机场约束数据");
                // 不再设置错误，允许空数据
            } else {
                setConstraintsError(null);
            }

            // 更新状态
            setAirportConstraints(airportConstraintsMap);
        } catch (error) {
            console.error("加载机场约束信息失败:", error);
            setConstraintsError(error.message || "加载机场约束信息失败");

            // 失败时不要回写：页面上已有的过站/宵禁必须保住。
            // 这里不能用函数式更新——监控页传入的 setter 接不住函数，会把约束写成不可用。
            // 初次加载失败时内存本来就是空的，后续按缺配置拦住即可。
            message.warning("加载机场约束信息失败，将使用页面上已有的过站配置；若尚未加载成功，自动计算时间将被拦住");
        } finally {
            setConstraintsLoading(false);
        }
    }

    /**
     * 加载所有机场约束信息
     * @param {Object} options - 选项对象
     * @param {Function} options.setConstraintsLoading - 设置加载状态的函数
     * @param {Function} options.setAirportConstraints - 设置机场约束的函数
     * @param {Function} options.setConstraintsError - 设置错误信息的函数
     */
    static async loadAllAirportConstraints({ setConstraintsLoading, setAirportConstraints, setConstraintsError }) {
        setConstraintsLoading(true);

        try {
            // 调用正确的 API 获取数据
            const response = await masterDataService.airport.getAllAirportConstraints(); // 使用 masterDataService

            // 正确检查 response.data 是否是数组
            let allConstraints = [];
            if (response && Array.isArray(response.data)) {
                allConstraints = response.data;
                if (allConstraints.length === 0) {
                    console.warn("API 返回了空的机场约束列表");
                }
            } else {
                // API 未返回预期的数据结构
                console.warn("获取所有机场约束 API 未返回有效的数组数据:", response);
                allConstraints = []; // 保证 allConstraints 是数组，后续逻辑能安全处理
            }

            // 转换为以机场代码为键的对象格式
            const airportConstraintsMap = {};

            allConstraints.forEach((constraint) => {
                const code = normalizeAirportCode(constraint.iata_code);
                if (!code) {
                    console.warn("跳过没有IATA代码的机场约束");
                    return;
                }

                airportConstraintsMap[code] = {
                    iata_code: code,
                    airport_name: constraint.airport_name || code,
                    timezone: constraint.timezone || "UTC",
                    dep_curfew_rules: this.convertCurfewRuleFields(constraint.dep_curfew_rules || []),
                    arr_curfew_rules: this.convertCurfewRuleFields(constraint.arr_curfew_rules || []),
                    turnaround_time: constraint.turnaround_time ?? null,
                    is_maintenance_base: constraint.is_maintenance_base || false,
                };
            });

            // 检查是否为空对象
            if (Object.keys(airportConstraintsMap).length === 0) {
                console.warn("处理后没有有效的机场约束数据");
            } else {
                setConstraintsError(null);
            }

            // 更新状态
            setAirportConstraints(airportConstraintsMap);
        } catch (error) {
            console.error("加载所有机场约束信息失败:", error);
            setConstraintsError(error.message || "加载所有机场约束信息失败");

            // 失败时不要清空已加载的约束，否则表格会突然变成一片「过站配置缺失」
            message.warning("加载机场约束信息失败，将使用页面上已有的过站配置；若尚未加载成功，自动计算时间将被拦住");
        } finally {
            setConstraintsLoading(false);
        }
    }

    /**
     * 按给定日期判断一条宵禁规则是否适用（星期、有效期、豁免名单）。
     * 语义与 checkCurfewViolation 内部的过滤完全一致（镜像实现，不改动共用方法本身）：
     * - weekly：weekdays 必须包含当天星期；valid_from / valid_to 有效期检查
     * - specific：只有 date_start 和 date_end 同时存在时才按日期范围过滤
     * - exceptions：豁免飞机跳过
     * @param {Object} rule - 宵禁规则
     * @param {Object} dateTimeObj - dayjs UTC 对象（以它所在的"天"判断适用性）
     * @param {string} [aircraftReg] - 飞机注册号
     * @returns {boolean} 规则是否适用
     */
    static isCurfewRuleApplicableOnDate(rule, dateTimeObj, aircraftReg) {
        if (!rule || !dateTimeObj || !dateTimeObj.isValid?.()) {
            return false;
        }

        const type = rule.type || "daily";
        const weekdayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
        const currentWeekday = weekdayMap[dateTimeObj.day()];
        const currentDate = dateTimeObj.format("YYYY-MM-DD");

        if (type === "weekly") {
            if (!rule.weekdays || rule.weekdays.length === 0 || !rule.weekdays.includes(currentWeekday)) {
                return false;
            }
            if (rule.valid_from && rule.valid_to) {
                if (currentDate < rule.valid_from || currentDate > rule.valid_to) {
                    return false;
                }
            } else if (rule.valid_from && currentDate < rule.valid_from) {
                return false;
            } else if (rule.valid_to && currentDate > rule.valid_to) {
                return false;
            }
        } else if (type === "specific" && rule.date_start && rule.date_end) {
            if (currentDate < rule.date_start || currentDate > rule.date_end) {
                return false;
            }
        }

        if (aircraftReg && rule.exceptions && Array.isArray(rule.exceptions) && rule.exceptions.includes(aircraftReg)) {
            return false;
        }

        return true;
    }

    /**
     * 计算"距结束"方向规则适用性的锚定时间（宵禁开始那天）。
     * 刚结束的那场宵禁属于它开始的那天：跨夜规则（开始晚于结束）时是结束日的前一天。
     * @param {Date|string} dateTime - 检查时刻
     * @param {Object} rule - 宵禁规则
     * @param {number} minutesFromEnd - 距宵禁结束的分钟数（getMinutesFromCurfewEnd 的返回值）
     * @returns {Object|null} 锚定日的 dayjs UTC 对象
     */
    static getCurfewEndRuleAnchor(dateTime, rule, minutesFromEnd) {
        const timeObj = dayjs.utc(dateTime);
        if (!timeObj.isValid()) {
            return null;
        }
        const startMinutes = timeUtils.timeToMinutes(rule.start_time || rule.start);
        const endMinutes = timeUtils.timeToMinutes(rule.end_time || rule.end);
        // 先回退到宵禁结束时刻（自动处理跨零点回绕），跨夜规则再往前挪一天到宵禁开始日
        const endInstant = timeObj.subtract(minutesFromEnd, "minute");
        return startMinutes > endMinutes ? endInstant.subtract(1, "day") : endInstant;
    }

    /**
     * 只返回数据、失败即抛、不写任何状态的机场约束读取方法。
     * 供"重算前刷新约束"使用：与 loadAirportConstraints 不同，失败时不会用
     * 空宵禁的默认约束覆盖已有数据，由调用方决定成功后是否回写状态。
     * @param {Array<string>} airportCodes - 机场三字码数组
     * @param {Object} [options] - 选项
     * @param {number} [options.timeoutMs=5000] - 超时阈值（毫秒），超时按失败处理
     * @returns {Promise<Object>} 以机场代码为键的约束对象
     * @throws {Error} 接口调用失败或超时
     */
    static async fetchAirportConstraints(airportCodes, { timeoutMs = 5000 } = {}) {
        const codes = (airportCodes || []).filter(Boolean);
        if (codes.length === 0) {
            return {};
        }

        let timeoutId;
        let response;
        const requestPromise = masterDataService.airport.getAirportConstraints();
        // 竞速超时后落败的原请求若最终 reject，会成为 unhandled rejection（控制台告警），挂空 catch 吞掉
        requestPromise.catch(() => {});
        try {
            response = await Promise.race([
                requestPromise,
                new Promise((_, reject) => {
                    timeoutId = setTimeout(() => reject(new Error(`获取机场约束超时（${Math.round(timeoutMs / 1000)} 秒）`)), timeoutMs);
                }),
            ]);
        } finally {
            clearTimeout(timeoutId);
        }

        if (!response || !Array.isArray(response.data)) {
            throw new Error("获取机场约束 API 未返回有效的数组数据");
        }

        const airportConstraintsMap = {};
        codes.forEach((rawCode) => {
            const code = normalizeAirportCode(rawCode);
            if (!code) return;

            const constraint = response.data.find((item) => normalizeAirportCode(item.iata_code) === code);
            if (constraint) {
                airportConstraintsMap[code] = {
                    iata_code: code,
                    airport_name: constraint.airport_name || code,
                    timezone: constraint.timezone || "UTC",
                    dep_curfew_rules: this.convertCurfewRuleFields(constraint.dep_curfew_rules || []),
                    arr_curfew_rules: this.convertCurfewRuleFields(constraint.arr_curfew_rules || []),
                    turnaround_time: constraint.turnaround_time ?? null,
                    is_maintenance_base: constraint.is_maintenance_base || false,
                };
            } else {
                // 主数据里没有这个机场：与 loadAirportConstraints 成功路径一致，只按“无宵禁”占位，
                // 最小过站保持为空，由取值方按缺配置拦住
                airportConstraintsMap[code] = {
                    iata_code: code,
                    airport_name: code,
                    timezone: "UTC",
                    dep_curfew_rules: [],
                    arr_curfew_rules: [],
                    turnaround_time: null,
                    is_maintenance_base: false,
                };
            }
        });

        return airportConstraintsMap;
    }

    /**
     * 检查时间是否在宵禁时段内并找到下一个可用时间
     * 统一的宵禁规则检查方法，可由各个相关功能模块调用
     * @param {Date|string} dateTime - 需要检查的日期时间
     * @param {string} airportCode - 机场代码
     * @param {string} operation - 操作类型 'departure' 或 'arrival'
     * @param {Object} options - 选项
     * @returns {Object} 包含isViolated（是否违反宵禁）和nextAvailableTime（下一个可用时间）的结果
     */
    static checkCurfewViolation(dateTime, airportCode, operation, options = {}) {
        const { airportConstraints } = options;

        if (!dateTime || !airportCode || !operation) {
            return { isViolated: false, nextAvailableTime: null };
        }

        try {
            // 修复异常的ISO格式
            let formattedDateTime = dateTime;
            if (typeof dateTime === "string") {
                const invalidFormatRegex = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}):\d{2}(Z|[+-]\d{2}:\d{2})?/;
                if (invalidFormatRegex.test(dateTime)) {
                    formattedDateTime = dateTime.replace(invalidFormatRegex, "$1$2");
                }
            }

            // 找到相应机场的约束数据
            const airportData = getAirportConstraint(airportConstraints, airportCode);
            if (!airportData) {
                return { isViolated: false, nextAvailableTime: null };
            }

            // 获取宵禁规则
            const curfewRules = operation === "departure" ? airportData.dep_curfew_rules : operation === "arrival" ? airportData.arr_curfew_rules : [];

            if (!curfewRules || curfewRules.length === 0) {
                return { isViolated: false, nextAvailableTime: null };
            }

            // 获取完整的时间对象
            const dateTimeObj = dayjs.utc(formattedDateTime);
            if (!dateTimeObj.isValid()) {
                console.error("宵禁检查中发现无效日期:", formattedDateTime);
                return { isViolated: false, nextAvailableTime: null };
            }

            // 获取当前星期几和日期信息
            const dayOfWeek = dateTimeObj.day();
            const weekdayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
            const currentWeekday = weekdayMap[dayOfWeek];
            const currentDate = dateTimeObj.format("YYYY-MM-DD");

            // 提取时间部分，转换为分钟表示
            const timeMinutes = dateTimeObj.hour() * 60 + dateTimeObj.minute();

            // 检查是否在宵禁时间内并找到最早的结束时间
            let isViolated = false;
            let earliestEndTime = null;

            // 按开始时间排序规则
            const sortedRules = [...curfewRules].sort((a, b) => {
                const startA = timeUtils.timeToMinutes(a.start_time || a.start);
                const startB = timeUtils.timeToMinutes(b.start_time || b.start);
                return startA - startB;
            });

            for (const rule of sortedRules) {
                // 检查规则类型适用性
                const type = rule.type || "daily";

                // 按规则类型检查适用性
                if (type === "weekly") {
                    if (!rule.weekdays || rule.weekdays.length === 0 || !rule.weekdays.includes(currentWeekday)) {
                        continue; // 不适用于当前星期
                    }
                    // 新增：检查每周规则的有效期
                    if (rule.valid_from && rule.valid_to) {
                        if (currentDate < rule.valid_from || currentDate > rule.valid_to) {
                            continue; // 不在有效期内
                        }
                    } else if (rule.valid_from && currentDate < rule.valid_from) {
                        continue; // 只有开始有效期，且当前日期在有效期开始之前
                    } else if (rule.valid_to && currentDate > rule.valid_to) {
                        continue; // 只有结束有效期，且当前日期在有效期结束之后
                    }
                } else if (type === "specific" && rule.date_start && rule.date_end) {
                    if (currentDate < rule.date_start || currentDate > rule.date_end) {
                        continue; // 不在日期范围内
                    }
                }

                // 检查例外飞机（如果options中提供了aircraft_reg）
                if (options.aircraft_reg && rule.exceptions && Array.isArray(rule.exceptions) && rule.exceptions.includes(options.aircraft_reg)) {
                    continue; // 如果是例外飞机，跳过此宵禁规则
                }

                // 检查时间
                const startTime = rule.start_time || rule.start;
                const endTime = rule.end_time || rule.end;

                if (!startTime || !endTime) continue;

                // 转换为分钟
                const start = timeUtils.timeToMinutes(startTime);
                const end = timeUtils.timeToMinutes(endTime);

                // 检查是否在宵禁时间内
                let inThisRule = false;
                if (start <= end) {
                    // 当天宵禁
                    inThisRule = timeMinutes >= start && timeMinutes < end;
                } else {
                    // 跨天宵禁
                    inThisRule = timeMinutes >= start || timeMinutes < end;
                }

                if (inThisRule) {
                    isViolated = true;

                    // 计算宵禁结束时间
                    const endMinutes = timeUtils.timeToMinutes(endTime);

                    let endTimeObj;
                    if (start > end) {
                        // 跨天宵禁
                        if (timeMinutes >= start) {
                            // 当前时间在宵禁开始后，结束时间在第二天
                            endTimeObj = dateTimeObj
                                .add(1, "day")
                                .startOf("day")
                                .add(Math.floor(endMinutes / 60), "hour")
                                .add(endMinutes % 60, "minute");
                        } else {
                            // 当前已经是第二天
                            endTimeObj = dateTimeObj
                                .startOf("day")
                                .add(Math.floor(endMinutes / 60), "hour")
                                .add(endMinutes % 60, "minute");
                        }
                    } else {
                        // 当天宵禁
                        endTimeObj = dateTimeObj
                            .startOf("day")
                            .add(Math.floor(endMinutes / 60), "hour")
                            .add(endMinutes % 60, "minute");
                    }

                    // 更新最早结束时间
                    if (!earliestEndTime || endTimeObj.isBefore(earliestEndTime)) {
                        earliestEndTime = endTimeObj;
                    }
                }
            }

            return {
                isViolated,
                nextAvailableTime: earliestEndTime ? earliestEndTime.toDate() : null,
            };
        } catch (error) {
            console.error("宵禁检查发生错误:", error);
            return { isViolated: false, nextAvailableTime: null };
        }
    }

    /**
     * 重构：使用checkCurfewViolation实现isInCurfew方法
     * 检查时间是否在宵禁时段内
     * @param {Date|string} dateTime - 日期时间
     * @param {string} airportCode - 机场代码
     * @param {string} operation - 操作类型 'departure' 或 'arrival'
     * @param {Object} options - 选项
     * @returns {boolean} 是否在宵禁时间内
     */
    static isInCurfew(dateTime, airportCode, operation, options = {}) {
        const result = this.checkCurfewViolation(dateTime, airportCode, operation, options);
        return result.isViolated;
    }

    /**
     * 校验航班是否违反宵禁规则或接近宵禁时间
     * @param {Object} flight - 航班对象
     * @param {Object} airportConstraints - 机场约束对象
     * @param {Object} parameters - 计划参数，包含近宵禁提醒时间
     * @param {Object} options - 额外选项
     * @param {string} options.timeZone - 当前时区设置
     * @param {Object} options.airportsMap - 机场映射数据
     * @returns {Object} 校验结果
     */
    static validateCurfewRules(flight, airportConstraints, parameters = {}, options = {}) {
        if (!flight || !airportConstraints) {
            return { isValid: true, messages: [] };
        }

        const violations = [];
        const warnings = [];
        const notices = [];
        const { departure_airport, arrival_airport, std, sta, aircraft_reg } = flight;
        const { timeZone = TIMEZONE_OPTIONS.UTC, airportsMap = {} } = options;

        // 获取近宵禁提醒配置
        const {
            curfew_warning_minutes = 30, // 黄色警告（默认30分钟）
            curfew_notice_minutes = 60, // 蓝色提示（默认60分钟）
        } = parameters || {};

        // 检查是否排除宵禁约束
        const excludedAirports = parameters?.curfew_constraints || [];
        const isDepAirportExcluded = excludedAirports.includes(departure_airport);
        const isArrAirportExcluded = excludedAirports.includes(arrival_airport);

        // 显示时区感知的时间
        const displayTimeWithZone = (dateTime, airportCode) => {
            if (!dateTime) return "";
            const timezone = getTimezoneByMode(timeZone, airportCode, airportsMap);
            return formatTimeWithZone(dateTime, timezone, "HH:mm");
        };

        // 根据当前时区格式化宵禁规则时间范围
        const formatCurfewTimeRangeWithZone = (rule, airportCode) => {
            if (!rule) return "未定义";

            const startTime = rule.start_time || rule.start;
            const endTime = rule.end_time || rule.end;

            if (!startTime || !endTime) return "未定义";

            try {
                const today = dayjs().format("YYYY-MM-DD");
                const startDateTime = `${today}T${formatTimeToHHMM(startTime)}:00Z`;
                const endDateTime = `${today}T${formatTimeToHHMM(endTime)}:00Z`;
                const formattedStart = displayTimeWithZone(startDateTime, airportCode);
                const formattedEnd = displayTimeWithZone(endDateTime, airportCode);
                const startMinutes = timeUtils.timeToMinutes(startTime);
                const endMinutes = timeUtils.timeToMinutes(endTime);

                if (startMinutes > endMinutes) {
                    return `${formattedStart}-${formattedEnd} (+1)`;
                }

                return `${formattedStart}-${formattedEnd}`;
            } catch (error) {
                console.error("格式化宵禁时间范围出错:", error, rule);
                return timeUtils.formatTimeRangeRule(rule);
            }
        };

        // 检查起飞机场的宵禁规则
        const depCode = normalizeAirportCode(departure_airport);
        const depAirport = getAirportConstraint(airportConstraints, depCode);
        if (depAirport && !isDepAirportExcluded) {
            const depTime = timeUtils.extractTimeFromDateTime(std);
            const zonedDepTime = displayTimeWithZone(std, depCode);

            // 使用统一的宵禁检查方法
            const depCurfewResult = this.checkCurfewViolation(std, departure_airport, "departure", {
                airportConstraints,
                aircraft_reg,
            });

            if (depCurfewResult.isViolated) {
                // 找到对应的规则用于显示
                const violatedRule = depAirport.dep_curfew_rules?.find((rule) => {
                    const startTime = rule.start_time || rule.start;
                    const endTime = rule.end_time || rule.end;
                    const timeObj = dayjs.utc(std);
                    const timeMinutes = timeObj.hour() * 60 + timeObj.minute();
                    const start = timeUtils.timeToMinutes(startTime);
                    const end = timeUtils.timeToMinutes(endTime);

                    // 检查规则类型适用性
                    const ruleType = rule.type || "daily";
                    const currentDate = timeObj.format("YYYY-MM-DD");
                    if (ruleType === "weekly") {
                        const dayOfWeek = timeObj.day();
                        const weekdayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                        const currentWeekday = weekdayMap[dayOfWeek];
                        if (!rule.weekdays || rule.weekdays.length === 0 || !rule.weekdays.includes(currentWeekday)) {
                            return false;
                        }
                        // 新增：检查每周规则的有效期
                        if (rule.valid_from && rule.valid_to) {
                            if (currentDate < rule.valid_from || currentDate > rule.valid_to) {
                                return false; // 不在有效期内
                            }
                        } else if (rule.valid_from && currentDate < rule.valid_from) {
                            return false; // 只有开始有效期，且当前日期在有效期开始之前
                        } else if (rule.valid_to && currentDate > rule.valid_to) {
                            return false; // 只有结束有效期，且当前日期在有效期结束之后
                        }
                    } else if (ruleType === "specific") {
                        if (!rule.date_start || !rule.date_end || currentDate < rule.date_start || currentDate > rule.date_end) {
                            return false;
                        }
                    }

                    // 检查是否是例外飞机
                    if (aircraft_reg && rule.exceptions && Array.isArray(rule.exceptions) && rule.exceptions.includes(aircraft_reg)) {
                        return false;
                    }

                    // 检查时间是否在宵禁范围内
                    let inThisRule = false;
                    if (start <= end) {
                        inThisRule = timeMinutes >= start && timeMinutes < end;
                    } else {
                        inThisRule = timeMinutes >= start || timeMinutes < end;
                    }
                    return inThisRule;
                });

                if (violatedRule) {
                    const formattedTimeRange = formatCurfewTimeRangeWithZone(violatedRule, departure_airport);
                    violations.push({
                        type: "departure_curfew",
                        airportCode: departure_airport,
                        message: `起飞时间 ${zonedDepTime} 违反起飞机场 ${departure_airport} 的宵禁规则: ${formattedTimeRange}`,
                        rule: violatedRule,
                    });
                } else {
                    // 如果找不到具体规则，创建一个通用的违规记录
                    violations.push({
                        type: "departure_curfew",
                        airportCode: departure_airport,
                        message: `起飞时间 ${zonedDepTime} 违反起飞机场 ${departure_airport} 的宵禁规则`,
                    });
                }
            }

            // 检查是否接近宵禁
            if (depAirport.dep_curfew_rules && depAirport.dep_curfew_rules.length > 0) {
                for (const rule of depAirport.dep_curfew_rules) {
                    const startTime = rule.start_time || rule.start;
                    const endTime = rule.end_time || rule.end;

                    // 检查规则是否适用于当前日期
                    const timeObj = dayjs.utc(std);
                    const dayOfWeek = timeObj.day();
                    const weekdayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                    const currentWeekday = weekdayMap[dayOfWeek];
                    const currentDate = timeObj.format("YYYY-MM-DD");

                    const ruleType = rule.type || "daily";
                    if (ruleType === "weekly") {
                        if (!rule.weekdays || rule.weekdays.length === 0 || !rule.weekdays.includes(currentWeekday)) {
                            continue;
                        }
                        // 新增：检查每周规则的有效期
                        if (rule.valid_from && rule.valid_to) {
                            if (currentDate < rule.valid_from || currentDate > rule.valid_to) {
                                continue; // 不在有效期内
                            }
                        } else if (rule.valid_from && currentDate < rule.valid_from) {
                            continue; // 只有开始有效期，且当前日期在有效期开始之前
                        } else if (rule.valid_to && currentDate > rule.valid_to) {
                            continue; // 只有结束有效期，且当前日期在有效期结束之后
                        }
                    } else if (ruleType === "specific") {
                        if (!rule.date_start || !rule.date_end || currentDate < rule.date_start || currentDate > rule.date_end) {
                            continue;
                        }
                    }

                    // 检查例外飞机
                    if (aircraft_reg && rule.exceptions && Array.isArray(rule.exceptions) && rule.exceptions.includes(aircraft_reg)) {
                        continue;
                    }

                    // 如果不在宵禁时间内，检查是否接近宵禁
                    const formattedTimeRange = formatCurfewTimeRangeWithZone(rule, departure_airport);
                    const minutesToCurfew = timeUtils.getMinutesToCurfew(depTime, rule);

                    if (minutesToCurfew !== null) {
                        // 黄色警告 - 接近宵禁临界点
                        if (minutesToCurfew <= curfew_warning_minutes) {
                            warnings.push({
                                type: "departure_curfew_warning",
                                airportCode: departure_airport,
                                message: `起飞时间 ${zonedDepTime} 距离 ${departure_airport} 的宵禁时间仅剩 ${minutesToCurfew} 分钟，规则：${formattedTimeRange}`,
                                rule,
                                minutesToCurfew,
                            });
                        }
                        // 蓝色提示 - 提前提醒
                        else if (minutesToCurfew <= curfew_notice_minutes) {
                            notices.push({
                                type: "departure_curfew_notice",
                                airportCode: departure_airport,
                                message: `起飞时间 ${zonedDepTime} 距离 ${departure_airport} 的宵禁时间还有 ${minutesToCurfew} 分钟，规则：${formattedTimeRange}`,
                                rule,
                                minutesToCurfew,
                            });
                        }
                    }
                }

                // 检查"刚过宵禁结束不足 N 分钟"（距结束方向）。
                // 跨夜规则的适用性按"宵禁开始那天"锚定（跨夜时是前一天），
                // 不能沿用上面按检查时刻所在天的过滤结果。
                // 时间已在任一条适用宵禁内（多规则场景）时不出该提示：
                // 红色违规已经足够，与后端 _check_flight_near_curfew_end、
                // 新版本地校验 buildNearCurfewIssues 的拦截口径一致。
                if (!depCurfewResult.isViolated) {
                    for (const rule of depAirport.dep_curfew_rules) {
                        const minutesFromCurfewEnd = timeUtils.getMinutesFromCurfewEnd(depTime, rule);
                        if (minutesFromCurfewEnd === null) continue;

                        const anchor = this.getCurfewEndRuleAnchor(std, rule, minutesFromCurfewEnd);
                        if (!anchor || !this.isCurfewRuleApplicableOnDate(rule, anchor, aircraft_reg)) continue;

                        const formattedTimeRange = formatCurfewTimeRangeWithZone(rule, departure_airport);
                        warnings.push({
                            type: "departure_curfew_end_warning",
                            airportCode: departure_airport,
                            message: `起飞时间 ${zonedDepTime} 距 ${departure_airport} 的宵禁结束仅 ${minutesFromCurfewEnd} 分钟，规则：${formattedTimeRange}`,
                            rule,
                            minutesFromCurfewEnd,
                        });
                    }
                }
            }
        }

        // 检查到达机场的宵禁规则
        const arrCode = normalizeAirportCode(arrival_airport);
        const arrAirport = getAirportConstraint(airportConstraints, arrCode);
        if (arrAirport && !isArrAirportExcluded) {
            const arrTime = timeUtils.extractTimeFromDateTime(sta);
            const zonedArrTime = displayTimeWithZone(sta, arrCode);

            // 使用统一的宵禁检查方法
            const arrCurfewResult = this.checkCurfewViolation(sta, arrival_airport, "arrival", {
                airportConstraints,
                aircraft_reg,
            });

            if (arrCurfewResult.isViolated) {
                // 找到对应的规则用于显示
                const violatedRule = arrAirport.arr_curfew_rules?.find((rule) => {
                    const startTime = rule.start_time || rule.start;
                    const endTime = rule.end_time || rule.end;
                    const timeObj = dayjs.utc(sta);
                    const timeMinutes = timeObj.hour() * 60 + timeObj.minute();
                    const start = timeUtils.timeToMinutes(startTime);
                    const end = timeUtils.timeToMinutes(endTime);

                    // 检查规则类型适用性
                    const ruleType = rule.type || "daily";
                    const currentDate = timeObj.format("YYYY-MM-DD");
                    if (ruleType === "weekly") {
                        const dayOfWeek = timeObj.day();
                        const weekdayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                        const currentWeekday = weekdayMap[dayOfWeek];
                        if (!rule.weekdays || rule.weekdays.length === 0 || !rule.weekdays.includes(currentWeekday)) {
                            return false;
                        }
                        // 新增：检查每周规则的有效期
                        if (rule.valid_from && rule.valid_to) {
                            if (currentDate < rule.valid_from || currentDate > rule.valid_to) {
                                return false; // 不在有效期内
                            }
                        } else if (rule.valid_from && currentDate < rule.valid_from) {
                            return false; // 只有开始有效期，且当前日期在有效期开始之前
                        } else if (rule.valid_to && currentDate > rule.valid_to) {
                            return false; // 只有结束有效期，且当前日期在有效期结束之后
                        }
                    } else if (ruleType === "specific") {
                        if (!rule.date_start || !rule.date_end || currentDate < rule.date_start || currentDate > rule.date_end) {
                            return false;
                        }
                    }

                    // 检查是否是例外飞机
                    if (aircraft_reg && rule.exceptions && Array.isArray(rule.exceptions) && rule.exceptions.includes(aircraft_reg)) {
                        return false;
                    }

                    // 检查时间是否在宵禁范围内
                    let inThisRule = false;
                    if (start <= end) {
                        inThisRule = timeMinutes >= start && timeMinutes < end;
                    } else {
                        inThisRule = timeMinutes >= start || timeMinutes < end;
                    }
                    return inThisRule;
                });

                if (violatedRule) {
                    const formattedTimeRange = formatCurfewTimeRangeWithZone(violatedRule, arrival_airport);
                    violations.push({
                        type: "arrival_curfew",
                        airportCode: arrival_airport,
                        message: `到达时间 ${zonedArrTime} 违反到达机场 ${arrival_airport} 的宵禁规则: ${formattedTimeRange}`,
                        rule: violatedRule,
                    });
                } else {
                    // 如果找不到具体规则，创建一个通用的违规记录
                    violations.push({
                        type: "arrival_curfew",
                        airportCode: arrival_airport,
                        message: `到达时间 ${zonedArrTime} 违反到达机场 ${arrival_airport} 的宵禁规则`,
                    });
                }
            }

            // 检查是否接近宵禁
            if (arrAirport.arr_curfew_rules && arrAirport.arr_curfew_rules.length > 0) {
                for (const rule of arrAirport.arr_curfew_rules) {
                    const startTime = rule.start_time || rule.start;
                    const endTime = rule.end_time || rule.end;

                    // 检查规则是否适用于当前日期
                    const timeObj = dayjs.utc(sta);
                    const dayOfWeek = timeObj.day();
                    const weekdayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                    const currentWeekday = weekdayMap[dayOfWeek];
                    const currentDate = timeObj.format("YYYY-MM-DD");

                    const ruleType = rule.type || "daily";
                    if (ruleType === "weekly") {
                        if (!rule.weekdays || rule.weekdays.length === 0 || !rule.weekdays.includes(currentWeekday)) {
                            continue;
                        }
                        // 新增：检查每周规则的有效期
                        if (rule.valid_from && rule.valid_to) {
                            if (currentDate < rule.valid_from || currentDate > rule.valid_to) {
                                continue; // 不在有效期内
                            }
                        } else if (rule.valid_from && currentDate < rule.valid_from) {
                            continue; // 只有开始有效期，且当前日期在有效期开始之前
                        } else if (rule.valid_to && currentDate > rule.valid_to) {
                            continue; // 只有结束有效期，且当前日期在有效期结束之后
                        }
                    } else if (ruleType === "specific") {
                        if (!rule.date_start || !rule.date_end || currentDate < rule.date_start || currentDate > rule.date_end) {
                            continue;
                        }
                    }

                    // 检查例外飞机
                    if (aircraft_reg && rule.exceptions && Array.isArray(rule.exceptions) && rule.exceptions.includes(aircraft_reg)) {
                        continue;
                    }

                    // 如果不在宵禁时间内，检查是否接近宵禁
                    const formattedTimeRange = formatCurfewTimeRangeWithZone(rule, arrival_airport);
                    const minutesToCurfew = timeUtils.getMinutesToCurfew(arrTime, rule);

                    if (minutesToCurfew !== null) {
                        // 黄色警告 - 接近宵禁临界点
                        if (minutesToCurfew <= curfew_warning_minutes) {
                            warnings.push({
                                type: "arrival_curfew_warning",
                                airportCode: arrival_airport,
                                message: `到达时间 ${zonedArrTime} 距离 ${arrival_airport} 的宵禁时间仅剩 ${minutesToCurfew} 分钟，规则：${formattedTimeRange}`,
                                rule,
                                minutesToCurfew,
                            });
                        }
                        // 蓝色提示 - 提前提醒
                        else if (minutesToCurfew <= curfew_notice_minutes) {
                            notices.push({
                                type: "arrival_curfew_notice",
                                airportCode: arrival_airport,
                                message: `到达时间 ${zonedArrTime} 距离 ${arrival_airport} 的宵禁时间还有 ${minutesToCurfew} 分钟，规则：${formattedTimeRange}`,
                                rule,
                                minutesToCurfew,
                            });
                        }
                    }
                }

                // 检查"刚过宵禁结束不足 N 分钟"（距结束方向），锚定宵禁开始日。
                // 已在任一条适用宵禁内时不出该提示（与起飞方向、后端、新版本地校验口径一致）
                if (!arrCurfewResult.isViolated) {
                    for (const rule of arrAirport.arr_curfew_rules) {
                        const minutesFromCurfewEnd = timeUtils.getMinutesFromCurfewEnd(arrTime, rule);
                        if (minutesFromCurfewEnd === null) continue;

                        const anchor = this.getCurfewEndRuleAnchor(sta, rule, minutesFromCurfewEnd);
                        if (!anchor || !this.isCurfewRuleApplicableOnDate(rule, anchor, aircraft_reg)) continue;

                        const formattedTimeRange = formatCurfewTimeRangeWithZone(rule, arrival_airport);
                        warnings.push({
                            type: "arrival_curfew_end_warning",
                            airportCode: arrival_airport,
                            message: `到达时间 ${zonedArrTime} 距 ${arrival_airport} 的宵禁结束仅 ${minutesFromCurfewEnd} 分钟，规则：${formattedTimeRange}`,
                            rule,
                            minutesFromCurfewEnd,
                        });
                    }
                }
            }
        }

        // 如果没有任何违规、警告或提示
        if (violations.length === 0 && warnings.length === 0 && notices.length === 0) {
            return { isValid: true, messages: [] };
        }

        // 返回组合结果
        return {
            isValid: violations.length === 0,
            violationTypes: [...violations.map((v) => v.type), ...warnings.map((v) => v.type), ...notices.map((v) => v.type)],
            messages: [...violations.map((v) => v.message), ...warnings.map((v) => v.message), ...notices.map((v) => v.message)],
            airportCodes: [...violations.map((v) => v.airportCode), ...warnings.map((v) => v.airportCode), ...notices.map((v) => v.airportCode)],
            severity: violations.length > 0 ? "error" : warnings.length > 0 ? "warning" : "notice",
            hasViolations: violations.length > 0,
            hasWarnings: warnings.length > 0,
            hasNotices: notices.length > 0,
            violations,
            warnings,
            notices,
        };
    }

    /**
     * 校验航班对是否满足最小地面时间要求
     * @param {Object} flight1 - 第一个航班对象
     * @param {Object} flight2 - 第二个航班对象
     * @param {number} minGroundTime - 最小地面时间（分钟）
     * @returns {Object} 校验结果
     */
    static validateGroundTime(flight1, flight2, minGroundTime) {
        if (!flight1 || !flight2 || !minGroundTime) {
            return { isValid: true, messages: [] };
        }

        // 检查是否为同一架飞机、在同一机场连接
        if (flight1.aircraft_reg !== flight2.aircraft_reg || flight1.arrival_airport !== flight2.departure_airport) {
            return { isValid: true, messages: [] };
        }

        const groundTimeMinutes = timeUtils.calculateGroundTime(flight1, flight2);
        const isValid = groundTimeMinutes >= minGroundTime;

        return {
            isValid,
            violations: isValid
                ? []
                : [
                      {
                          type: "ground_time",
                          message: `地面时间 ${Math.floor(groundTimeMinutes / 60)}h${groundTimeMinutes % 60}m 小于要求的最小地面时间 ${Math.floor(minGroundTime / 60)}h${minGroundTime % 60}m`,
                          severity: "warning",
                      },
                  ],
        };
    }

    /**
     * 校验最小过站时间约束
     *
     * 只认到达站在机场主数据里的明确配置（含 0 分钟）。航班上存着的
     * min_ground_time_minutes 不参与判断——那可能是以前自动填时刻写下的假 1 小时，
     * 拿它当尺子会把“缺配置”伪装成“过站正常”。机场没配时按“过站配置缺失”报出。
     *
     * @param {Array} flights - 航班数组
     * @param {Object} airportConstraints - 机场约束对象
     * @returns {Array} 违反最小过站时间（或缺少配置）的航班对列表
     */
    static validateMinGroundTime(flights, airportConstraints) {
        if (!flights || flights.length < 2 || !airportConstraints) {
            return [];
        }

        const violations = [];
        const sortedFlights = [...flights].sort((a, b) => new Date(a.std) - new Date(b.std));

        for (let i = 0; i < sortedFlights.length - 1; i++) {
            const currFlight = sortedFlights[i];
            const nextFlight = sortedFlights[i + 1];
            const currentArrivalAirport = currFlight.arrival_airport || currFlight.arr_airport;
            const nextDepartureAirport = nextFlight.departure_airport || nextFlight.dep_airport;

            // 过站是同一架飞机的概念：与 ground_time_minutes（本班 STA → 下一班 STD）
            // 的口径保持一致；新版按飞机分组校验，这里也只在同机时判断
            if (currFlight.aircraft_reg !== nextFlight.aircraft_reg) {
                continue;
            }

            // 如果当前航班到达机场和下一个航班的起飞机场相同，检查地面时间
            const currArr = normalizeAirportCode(currentArrivalAirport);
            const nextDep = normalizeAirportCode(nextDepartureAirport);
            if (!currArr || currArr !== nextDep) {
                continue;
            }

            const airport = currArr;
            const minGroundTime = getAirportMinGroundTime(airportConstraints, airport);
            const currArrival = new Date(currFlight.sta);
            const nextDeparture = new Date(nextFlight.std);

            // 计算地面时间（分钟）
            const groundTimeMinutes = (nextDeparture - currArrival) / (1000 * 60);
            const pairFlightIds = [currFlight.id, nextFlight.id];

            if (minGroundTime === null) {
                violations.push({
                    flights: pairFlightIds,
                    flightIds: pairFlightIds,
                    prevFlightId: currFlight.id,
                    nextFlightId: nextFlight.id,
                    airport,
                    actualTime: groundTimeMinutes,
                    requiredTime: null,
                    type: "min_ground_time_missing",
                    violationType: "min_ground_time_missing",
                    message: `在 ${airport} 未配置最小过站时间，无法判断过站是否满足要求，请先到机场主数据维护最小过站时间`,
                    severity: "warning",
                });
                continue;
            }

            if (groundTimeMinutes < minGroundTime) {
                violations.push({
                    flights: pairFlightIds,
                    flightIds: pairFlightIds,
                    prevFlightId: currFlight.id,
                    nextFlightId: nextFlight.id,
                    airport,
                    actualTime: groundTimeMinutes,
                    requiredTime: minGroundTime,
                    type: "ground_time",
                    violationType: "ground_time",
                    message: `在 ${airport} 的地面时间 ${Math.round(groundTimeMinutes)} 分钟小于最小过站时间 ${minGroundTime} 分钟`,
                    severity: "warning",
                });
            }
        }

        return violations;
    }

    /**
     * 校验航班计划
     * @param {Array} flights - 航班数组
     * @param {Object} airportConstraints - 机场约束对象
     * @param {Object} parameters - 计划参数
     * @param {Object} options - 额外选项
     * @param {string} options.timeZone - 当前时区设置
     * @param {Object} options.airportsMap - 机场映射数据
     * @returns {Array} 校验结果数组
     */
    static validateSchedule(flights, airportConstraints, parameters = {}, options = {}) {
        if (!flights || flights.length === 0) {
            return [];
        }
        const results = [];
        const normalizedAirportConstraints = airportConstraints || {};
        const hasAirportConstraints = Object.keys(normalizedAirportConstraints).length > 0;

        // 校验每个航班的宵禁规则
        if (hasAirportConstraints) {
            flights.forEach((flight) => {
                const curfewValidation = this.validateCurfewRules(flight, normalizedAirportConstraints, parameters, options);

                if (curfewValidation.hasViolations) {
                    results.push({
                        flightId: flight.id,
                        severity: "error",
                        violationType: "curfew",
                        violationTypes: curfewValidation.violations.map((v) => v.type),
                        messages: curfewValidation.violations.map((v) => v.message),
                        airportCodes: curfewValidation.violations.map((v) => v.airportCode),
                    });
                }

                if (curfewValidation.hasWarnings) {
                    results.push({
                        flightId: flight.id,
                        severity: "warning",
                        violationType: "curfew_warning",
                        violationTypes: curfewValidation.warnings.map((v) => v.type),
                        messages: curfewValidation.warnings.map((v) => v.message),
                        airportCodes: curfewValidation.warnings.map((v) => v.airportCode),
                        minutesToCurfew: curfewValidation.warnings.map((v) => v.minutesToCurfew),
                    });
                }

                if (curfewValidation.hasNotices) {
                    results.push({
                        flightId: flight.id,
                        severity: "notice",
                        violationType: "curfew_notice",
                        violationTypes: curfewValidation.notices.map((v) => v.type),
                        messages: curfewValidation.notices.map((v) => v.message),
                        airportCodes: curfewValidation.notices.map((v) => v.airportCode),
                        minutesToCurfew: curfewValidation.notices.map((v) => v.minutesToCurfew),
                    });
                }
            });
        }

        // 校验最小过站时间 (地面时间)
        if (hasAirportConstraints) {
            const groundTimeResults = this.validateMinGroundTime(flights, normalizedAirportConstraints);
            results.push(...groundTimeResults);
        }

        // 校验航班连续性
        const continuityResults = this.validateFlightsContinuity(flights);
        results.push(...continuityResults);

        results.push(...this.validateAircraftAvailability(
            flights,
            options.aircraftAvailabilityWindowsByRegistration || {},
        ));

        return results;
    }

    static validateAircraftAvailability(
        flights,
        windowsByRegistration = {},
    ) {
        const results = [];
        flights.forEach((flight) => {
            const aircraftReg = normalizeAircraftRegistration(flight.aircraft_reg);
            if (!aircraftReg || !flight.std || !flight.sta || new Date(flight.std) >= new Date(flight.sta)) {
                return;
            }
            evaluateAircraftAvailability({
                flightStart: flight.std,
                flightEnd: flight.sta,
                windows: getAircraftAvailabilityWindows(windowsByRegistration, aircraftReg),
            }).forEach((evaluation) => {
                const window = evaluation.window || {};
                const label = getAvailabilityWindowLabel(window);
                const common = {
                    flightId: flight.id,
                    aircraftReg,
                    windowId: window.id || window.window_id || null,
                    windowType: window.window_type || null,
                    windowTitle: window.title || null,
                    airportCode: window.airport_code || null,
                    windowStart: evaluation.windowStart.toISOString(),
                    windowEnd: evaluation.windowEnd?.toISOString?.() || null,
                    flightStart: evaluation.flightStart.toISOString(),
                    flightEnd: evaluation.flightEnd.toISOString(),
                };
                if (evaluation.relation === "overlap") {
                    results.push({
                        ...common,
                        severity: "error",
                        violationType: "aircraft_unavailable",
                        violationTypes: ["aircraft_unavailable"],
                        messages: [`飞机不可用：${flight.flight_number || flight.id} 与 ${label}窗口重叠`],
                        overlapStart: evaluation.overlapStart.toISOString(),
                        overlapEnd: evaluation.overlapEnd.toISOString(),
                    });
                    return;
                }
                results.push({
                    ...common,
                    severity: "warning",
                    violationType: "aircraft_unavailable_near",
                    violationTypes: ["aircraft_unavailable_near"],
                    messages: [`飞机可用性窗口临近：${flight.flight_number || flight.id} 距 ${label} ${evaluation.gapMinutes} 分钟`],
                    gapMinutes: evaluation.gapMinutes,
                    nearSide: evaluation.nearSide,
                });
            });
        });
        return results;
    }

    /**
     * 保存前基于当前航班同步计算飞机可用性状态。
     * 不复用防抖生成的 validationResults，避免用户修改航班后立即保存时读取旧结果。
     * 未填写航段机号的航班不推断任何计划级飞机，也不参与飞机可用性校验。
     */
    static getAircraftAvailabilitySaveStatus(flights, availabilityState = {}) {
        const expectedQuery = buildAircraftAvailabilityQuery({ flights });
        if (expectedQuery.registrations.length === 0) {
            return {
                conflicts: [],
                incomplete: false,
                expectedQuery,
            };
        }

        const loadedRegistrations = (availabilityState.registrations || [])
            .map(normalizeAircraftRegistration)
            .filter(Boolean)
            .sort();
        const queryMatches = JSON.stringify(loadedRegistrations) === JSON.stringify(expectedQuery.registrations)
            && availabilityState.queryStart === expectedQuery.start
            && availabilityState.queryEnd === expectedQuery.end;
        const conflicts = this.validateAircraftAvailability(
            flights,
            availabilityState.windowsByRegistration || {},
        ).filter(
            (result) => result.violationType === "aircraft_unavailable" && result.severity === "error",
        );

        return {
            conflicts,
            incomplete: Boolean(
                availabilityState.loading
                || availabilityState.fetching
                || availabilityState.error
                || availabilityState.stale
                || !queryMatches
            ),
            expectedQuery,
        };
    }

    /**
     * 校验航班是否连续
     * @param {Array} flights - 航班数组
     * @returns {Array} 连续性校验结果数组
     */
    static validateFlightsContinuity(flights) {
        if (!flights || flights.length < 2) {
            return [];
        }

        const results = [];
        // 按照起飞时间排序
        const sortedFlights = [...flights].sort((a, b) => new Date(a.std) - new Date(b.std));

        for (let i = 1; i < sortedFlights.length; i++) {
            const prevFlight = sortedFlights[i - 1];
            const currFlight = sortedFlights[i];

            // 如果是同一架飞机，检查连续性
            if (prevFlight.aircraft_reg === currFlight.aircraft_reg) {
                // 上一个航班的到达机场应该是当前航班的出发机场
                if (prevFlight.arrival_airport !== currFlight.departure_airport) {
                    results.push({
                        flightId: currFlight.id,
                        severity: "warning",
                        violationType: "flight_continuity",
                        messages: [`航班不连续：上一航班到达 ${prevFlight.arrival_airport}，当前航班从 ${currFlight.departure_airport} 出发`],
                        airportCodes: [currFlight.departure_airport],
                        prevFlightId: prevFlight.id,
                        prevAirport: prevFlight.arrival_airport,
                    });
                }
            }
        }

        return results;
    }

    // 替换现有的checkAndFindNextAvailableTime方法，使用统一的checkCurfewViolation方法
    static checkAndFindNextAvailableTime(dateTime, airportCode, operation, options = {}) {
        const result = this.checkCurfewViolation(dateTime, airportCode, operation, options);

        return {
            isInCurfew: result.isViolated,
            nextAvailableTime: result.isViolated && result.nextAvailableTime ? result.nextAvailableTime : dateTime,
        };
    }
}

export default ConstraintsService;
